import React, { useState, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { chatbotUrl } from '../../../util/config';

const MEDIA_TYPES = [
    { value:'image',    label:'Image',    icon:'bi-image',        accept:'image/jpeg,image/png,image/gif,image/webp', maxMB:5,  extensions:['jpg','jpeg','png','gif','webp'] },
    { value:'video',    label:'Video',    icon:'bi-play-circle',  accept:'video/mp4,video/webm',                      maxMB:50, extensions:['mp4','webm'] },
    { value:'document', label:'Document', icon:'bi-file-earmark', accept:'.pdf,.docx,.pptx,.xlsx',                    maxMB:20, extensions:['pdf','docx','pptx','xlsx'] },
];

const inputStyle = {
    width:'100%', padding:'8px 10px', border:'1px solid #e5e7eb',
    borderRadius:8, fontSize:12, color:'#111827', outline:'none', boxSizing:'border-box',
};

export default function MediaEditor({ url, setUrl, type, setType, caption, setCaption }) {
    const [tab,       setTab]       = useState('url');
    const [uploading, setUploading] = useState(false);
    const [progress,  setProgress]  = useState(0);
    const [preview,   setPreview]   = useState(null);
    const fileRef = useRef(null);

    const mediaMeta = MEDIA_TYPES.find(m => m.value === type) || MEDIA_TYPES[0];

    const handleUrlChange = (val) => { setUrl(val); setPreview(null); };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const maxBytes = mediaMeta.maxMB * 1024 * 1024;
        if (file.size > maxBytes) { toast.error(`File too large. Max size for ${mediaMeta.label}: ${mediaMeta.maxMB}MB`); return; }

        const ext = file.name.split('.').pop().toLowerCase();
        if (!mediaMeta.extensions.includes(ext)) { toast.error(`Invalid file type. Allowed: ${mediaMeta.extensions.join(', ')}`); return; }

        if (type === 'image') setPreview(URL.createObjectURL(file));

        setUploading(true); setProgress(0);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', file);
            formData.append('media_type', type);

            const xhr = new XMLHttpRequest();
            xhr.upload.onprogress = (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); };

            await new Promise((resolve, reject) => {
                xhr.open('POST', `${chatbotUrl}/media/upload`);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.onload = () => {
                    const data = JSON.parse(xhr.responseText);
                    if (xhr.status >= 200 && xhr.status < 300) {
                        const uploadedUrl = data?.data?.url || data?.url;
                        if (uploadedUrl) { setUrl(uploadedUrl); toast.success('File uploaded successfully'); }
                        resolve();
                    } else reject(new Error(data?.detail || 'Upload failed'));
                };
                xhr.onerror = () => reject(new Error('Upload failed'));
                xhr.send(formData);
            });
        } catch (err) {
            toast.error(err.message || 'Upload failed');
            setPreview(null);
        } finally {
            setUploading(false); setProgress(0);
        }
    };

    const previewUrl = preview || url;
    const renderPreview = () => {
        if (!previewUrl) return null;
        if (type === 'image') return (
            <div style={{ marginTop:8, borderRadius:8, overflow:'hidden', border:'1px solid #e5e7eb' }}>
                <img src={previewUrl} alt="preview" style={{ width:'100%', maxHeight:140, objectFit:'cover', display:'block' }}
                    onError={e => e.target.style.display='none'}/>
            </div>
        );
        if (type === 'video') return (
            <div style={{ marginTop:8, borderRadius:8, overflow:'hidden', border:'1px solid #e5e7eb', background:'#000' }}>
                <video src={previewUrl} controls style={{ width:'100%', maxHeight:180, display:'block' }}
                    onMouseDown={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}/>
            </div>
        );
        if (type === 'document') {
            const filename = previewUrl.split('/').pop();
            const ext = filename.split('.').pop().toUpperCase();
            return (
                <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'#f8fafc', borderRadius:8, border:'1px solid #e5e7eb' }}>
                    <div style={{ width:36, height:36, borderRadius:8, background:'#1565c0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#fff', flexShrink:0 }}>{ext}</div>
                    <p style={{ fontSize:11, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', margin:0 }}>{filename}</p>
                </div>
            );
        }
    };

    return (
        <div style={{ padding:16, display:'flex', flexDirection:'column', gap:14 }}>
            {/* Media type */}
            <div>
                <label style={lbl}>Media Type</label>
                <div style={{ display:'flex', gap:6 }}>
                    {MEDIA_TYPES.map(mt => (
                        <button key={mt.value} onClick={() => { setType(mt.value); setUrl(''); setPreview(null); }}
                            style={{ flex:1, padding:'8px 4px', fontSize:11, fontWeight:600, borderRadius:8, cursor:'pointer',
                                border: type===mt.value ? '2px solid #1565c0' : '2px solid #e5e7eb',
                                background: type===mt.value ? '#eff6ff' : '#f8fafc',
                                color: type===mt.value ? '#1565c0' : '#6b7280',
                            }}>
                            <i className={`bi ${mt.icon}`} style={{ fontSize:16, display:'block', marginBottom:2 }}></i>
                            {mt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab */}
            <div style={{ display:'flex', background:'#f3f4f6', borderRadius:8, padding:3, gap:3 }}>
                {[{ id:'url', label:'Enter URL' }, { id:'upload', label:'Upload File' }].map(t => (
                    <button key={t.id} onClick={() => { setTab(t.id); setUrl(''); setPreview(null); }}
                        style={{ flex:1, padding:'7px 0', fontSize:12, fontWeight:600, borderRadius:6, border:'none', cursor:'pointer',
                            background: tab===t.id ? '#fff' : 'transparent',
                            color: tab===t.id ? '#111827' : '#6b7280',
                            boxShadow: tab===t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}>
                        <i className={`bi ${t.id === 'url' ? 'bi-link-45deg' : 'bi-upload'}`} style={{ marginRight:4 }}></i>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* URL tab */}
            {tab === 'url' && (
                <div>
                    <label style={lbl}>{mediaMeta.label} URL</label>
                    <input value={url||''} onChange={e => handleUrlChange(e.target.value)}
                        onKeyDown={e => e.stopPropagation()}
                        placeholder={`https://example.com/file.${mediaMeta.extensions[0]}`}
                        style={inputStyle}/>
                    {renderPreview()}
                </div>
            )}

            {/* Upload tab */}
            {tab === 'upload' && (
                <div>
                    <label style={lbl}>Upload {mediaMeta.label}</label>
                    <div onClick={() => !uploading && fileRef.current?.click()}
                        style={{ border:`2px dashed ${uploading ? '#1565c0' : '#e5e7eb'}`, borderRadius:10, padding:'20px 16px', textAlign:'center', cursor: uploading ? 'not-allowed' : 'pointer', background: uploading ? '#eff6ff' : '#f8fafc' }}>
                        {uploading ? (
                            <div>
                                <div style={{ fontSize:11, color:'#1565c0', fontWeight:600, marginBottom:8 }}>Uploading... {progress}%</div>
                                <div style={{ height:6, background:'#bfdbfe', borderRadius:3, overflow:'hidden' }}>
                                    <div style={{ height:'100%', width:`${progress}%`, background:'#1565c0', borderRadius:3, transition:'width 0.3s' }}/>
                                </div>
                            </div>
                        ) : url && tab === 'upload' ? (
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                                <i className="bi bi-check-circle-fill" style={{ color:'#16a34a', fontSize:16 }}></i>
                                <span style={{ fontSize:12, color:'#16a34a', fontWeight:600 }}>Uploaded</span>
                                <button onClick={e => { e.stopPropagation(); setUrl(''); setPreview(null); }}
                                    style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:14 }}>×</button>
                            </div>
                        ) : (
                            <>
                                <i className="bi bi-cloud-upload" style={{ fontSize:20, color:'#9ca3af', display:'block', margin:'0 auto 8px' }}></i>
                                <p style={{ fontSize:12, color:'#374151', fontWeight:500, margin:0 }}>Click to upload {mediaMeta.label.toLowerCase()}</p>
                                <p style={{ fontSize:10, color:'#9ca3af', marginTop:4 }}>{mediaMeta.extensions.join(', ').toUpperCase()} · Max {mediaMeta.maxMB}MB</p>
                            </>
                        )}
                    </div>
                    <input ref={fileRef} type="file" accept={mediaMeta.accept} style={{ display:'none' }} onChange={handleFileSelect}/>
                    {renderPreview()}
                </div>
            )}

            {/* Caption */}
            <div>
                <label style={lbl}>Caption <span style={{ fontWeight:400, color:'#9ca3af' }}>(optional)</span></label>
                <input value={caption||''} onChange={e => setCaption(e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    placeholder="Caption shown below the media..."
                    style={inputStyle}/>
            </div>
            <ToastContainer/>
        </div>
    );
}

const lbl = { fontSize:11, fontWeight:700, color:'#6b7280', display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' };
